"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { ChevronDown, LogOut, User } from "lucide-react";
import { MY_ACCOUNT_URL } from "@/utils/constants";
import { signOut } from "@/hooks/useSession";

function initialsOf(user) {
  return user?.initials || user?.name?.trim()?.charAt(0)?.toUpperCase() || "?";
}

/** Desktop header avatar + dropdown. */
export default function AccountMenu({ user }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) setIsOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const handleSignOut = async () => {
    if (isSigningOut) return;
    setIsSigningOut(true);
    await signOut();
    setIsOpen(false);
    setIsSigningOut(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label="Account menu"
        className="flex items-center gap-1.5 rounded-full p-0.5 pr-1.5 transition-colors hover:bg-neutral-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900 focus-visible:ring-offset-2"
      >
        <span className="flex h-8 w-8 lg:h-9 lg:w-9 items-center justify-center rounded-full bg-neutral-100 border border-neutral-200 text-[13px] geist-semibold text-[#171717]">
          {initialsOf(user)}
        </span>
        <ChevronDown
          className={`w-3.5 h-3.5 text-[#525252] transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-3 w-64 bg-white border border-neutral-200/70 rounded-2xl shadow-[0_12px_40px_rgba(0,0,0,0.08)] p-2"
        >
          <div className="flex items-center gap-3 px-3 py-2.5">
            <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-neutral-100 border border-neutral-200 text-[14px] geist-semibold text-[#171717]">
              {initialsOf(user)}
            </span>
            <p className="text-[14px] geist-semibold text-neutral-800 truncate">
              {user.name}
            </p>
          </div>

          <div className="my-1 h-px bg-neutral-200/70" />

          <Link
            href={MY_ACCOUNT_URL}
            target="_blank"
            rel="noopener noreferrer"
            role="menuitem"
            onClick={() => setIsOpen(false)}
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-[14px] geist-semibold text-neutral-800 transition-colors hover:bg-neutral-50/80 hover:text-[#171717]"
          >
            <User className="w-4 h-4 text-[#525252]" />
            My Account
          </Link>

          <button
            onClick={handleSignOut}
            disabled={isSigningOut}
            role="menuitem"
            className="flex w-full items-center gap-3 px-3 py-2.5 rounded-xl text-[14px] geist-semibold text-red-600 transition-colors hover:bg-red-50/80 disabled:opacity-60"
          >
            <LogOut className="w-4 h-4" />
            {isSigningOut ? "Signing out…" : "Sign Out"}
          </button>
        </div>
      )}
    </div>
  );
}

/** Flat account block for the mobile drawer - no nested dropdown. */
export function AccountMenuMobile({ user, onNavigate }) {
  const [isSigningOut, setIsSigningOut] = useState(false);

  const handleSignOut = async () => {
    if (isSigningOut) return;
    setIsSigningOut(true);
    await signOut();
    setIsSigningOut(false);
    onNavigate?.();
  };

  return (
    <div className="space-y-2.5 sm:space-y-3">
      <div className="flex items-center gap-3 px-1">
        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-neutral-100 border border-neutral-200 text-[14px] geist-semibold text-[#171717]">
          {initialsOf(user)}
        </span>
        <p className="text-[14px] geist-semibold text-[#171717] truncate">
          {user.name}
        </p>
      </div>

      <Link
        href={MY_ACCOUNT_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onNavigate}
        className="flex justify-center items-center w-full h-10 text-[14px] geist-semibold border border-[#E5E5E5] rounded-full text-neutral-700 hover:bg-[#F9F9F9] transition-colors"
      >
        My Account
      </Link>

      <button
        onClick={handleSignOut}
        disabled={isSigningOut}
        className="flex justify-center items-center w-full h-10 text-[14px] geist-semibold border border-[#E5E5E5] rounded-full text-red-600 hover:bg-red-50 transition-colors disabled:opacity-60"
      >
        {isSigningOut ? "Signing out…" : "Sign Out"}
      </button>
    </div>
  );
}
